/**
 * Metric tiles, section cards, legends, the card that is also a button, and skeletons.
 *
 * Every route has a skeleton matched to its final layout so nothing reflows on arrival. The
 * prototype has no loading state at all — every screen assumes data is present synchronously.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router';
import styles from './Surface.module.css';

export interface MetricTileProps {
  value: ReactNode;
  label: string;
  swatch?: 'inforce' | 'superseded';
}

export function MetricTile({ value, label, swatch }: MetricTileProps) {
  return (
    <div className={styles.metric}>
      <div className={styles.metricValue}>{value}</div>
      <div className={styles.metricLabel}>
        {swatch ? <Swatch tone={swatch} /> : null}
        {label}
      </div>
    </div>
  );
}

export interface MetricGridProps {
  columns?: 2 | 3 | 4;
  wide?: boolean;
  children: ReactNode;
}

export function MetricGrid({ columns = 4, wide = false, children }: MetricGridProps) {
  const columnClass =
    columns === 3 ? styles.metricsThree : columns === 2 ? styles.metricsTwo : undefined;
  return (
    <div
      className={[styles.metrics, columnClass, wide ? styles.metricsWide : undefined]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  );
}

export interface SectionCardProps {
  title: string;
  note?: ReactNode;
  /** Chart cards cap at 1034px to match the SVG's authored width. */
  maxWidth?: number;
  actions?: ReactNode;
  children: ReactNode;
}

export function SectionCard({ title, note, maxWidth, actions, children }: SectionCardProps) {
  return (
    <section
      className={styles.card}
      style={maxWidth === undefined ? undefined : { maxWidth }}
      aria-label={title}
    >
      <h2>{title}</h2>
      {note ? <p className={styles.cardNote}>{note}</p> : null}
      {actions}
      <div className={styles.cardBody}>{children}</div>
    </section>
  );
}

export interface SwatchProps {
  tone: 'inforce' | 'superseded';
  shape?: 'square' | 'bar';
}

export function Swatch({ tone, shape = 'square' }: SwatchProps) {
  return (
    <span
      aria-hidden="true"
      className={[
        styles.swatch,
        shape === 'bar' ? styles.swatchBar : undefined,
        tone === 'inforce' ? styles.swatchInForce : styles.swatchSuperseded,
      ]
        .filter(Boolean)
        .join(' ')}
    />
  );
}

export interface LegendProps {
  items: ReadonlyArray<{ key: ReactNode; label: string }>;
  'aria-label'?: string;
}

export function Legend({ items, ...rest }: LegendProps) {
  return (
    <div className={styles.legend} aria-label={rest['aria-label']}>
      {items.map((item) => (
        <span className={styles.legendItem} key={item.label}>
          {item.key}
          {item.label}
        </span>
      ))}
    </div>
  );
}

export interface CardButtonProps {
  /** Where the card goes. A card that is the way in to editing is a link to that route. */
  to: string;
  /** Reads as a sentence — "Edit Renewal term" — rather than the concatenated card contents. */
  'aria-label': string;
  actionLabel: string;
  trailing?: ReactNode;
  muted?: boolean;
  children: ReactNode;
}

export function CardButton({
  to,
  actionLabel,
  trailing,
  muted = false,
  children,
  ...rest
}: CardButtonProps) {
  return (
    <Link
      to={to}
      aria-label={rest['aria-label']}
      className={[styles.cardButton, muted ? styles.cardButtonMuted : undefined]
        .filter(Boolean)
        .join(' ')}
    >
      <span className={styles.cardAction} aria-hidden="true">
        {actionLabel}
      </span>
      {trailing ? <span className={styles.cardTrailing}>{trailing}</span> : null}
      {children}
    </Link>
  );
}

export function Skeleton({ height = 16, width }: { height?: number; width?: number | string }) {
  return (
    <span
      className={styles.skeleton}
      style={{ display: 'block', height, width: width ?? '100%' }}
      aria-hidden="true"
    />
  );
}

export function SkeletonStack({ rows = 3, height = 16 }: { rows?: number; height?: number }) {
  return (
    <div className={styles.skeletonStack}>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} height={height} />
      ))}
    </div>
  );
}
